import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Dropdown, makeStyles, Option } from "@fluentui/react-components";
import { useIntl } from "react-intl";
import { Speaker220Filled } from "@fluentui/react-icons";

const useStyles = makeStyles({
  label: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }
});

/**
 * PreferredSpeakerSelect Component
 *
 * Allows users to select their preferred speaker device from a dropdown.
 *
 * Props:
 * - isSpeakerEnabled (bool): Determines if the dropdown is enabled.
 * - speakerDevices (object): Contains the current speaker value and available options.
 *   - value (string): The currently selected speaker device value.
 *   - options (array): List of available speaker devices with `value` and `label`.
 * - onSpeakerChange (func): Callback function triggered when the speaker selection changes.
 */
export function PreferredSpeakerSelect({ isSpeakerEnabled, speakerDevices, onSpeakerChange }) {
  const intl = useIntl();
  const styles = useStyles();

  const preferredSpeakerValue = speakerDevices.value;
  const preferredSpeakerLabel = speakerDevices.options?.find(option => option.value === preferredSpeakerValue)?.label;
  const speakerOptions = speakerDevices.options;

  /**
   * Handler for when a new speaker option is selected.
   * Ensures that the selected value is different before triggering the callback.
   */
  const handleOptionSelect = useCallback(
    (_, data) => {
      if (data.optionValue !== preferredSpeakerValue) {
        onSpeakerChange(data.optionValue);
      }
    },
    [onSpeakerChange, preferredSpeakerValue]
  );

  return (
    <Dropdown
      placeholder={intl.formatMessage({ id: "preferredSpeakerSelect.placeholder", defaultMessage: "Select Speaker" })}
      onOptionSelect={handleOptionSelect}
      appearance="filled-darker"
      disabled={!isSpeakerEnabled}
      button={
        <span className={styles.label}>
          <Speaker220Filled />
          &nbsp;
          {preferredSpeakerLabel}
        </span>
      }
      value={preferredSpeakerLabel}
      selectedOptions={[preferredSpeakerValue]}
    >
      {speakerOptions &&
        speakerOptions.map(option => (
          <Option key={option.value} value={option.value} text={option.label}>
            <span className={styles.label}>{option.label}</span>
          </Option>
        ))}
    </Dropdown>
  );
}

PreferredSpeakerSelect.propTypes = {
  isSpeakerEnabled: PropTypes.bool.isRequired,
  speakerDevices: PropTypes.shape({
    value: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired,
  onSpeakerChange: PropTypes.func.isRequired
};
